import PropTypes from "prop-types";
import React from "react";

import { colors } from "../defines";
import { getColorVariable } from "../getColorVariable";
import { Transition } from "../Transition";
import styles from "./Ripple.module.scss";

const DELAY_FOR_2ND_RIPPLE = 0.15;
const DOT_SIZE = 16 * 6;
const STANDARD_DURATION = 0.4;

/**
 * The Ripple component shows a fading circle on the page that last less than a second. Ripple
 * requires the position css property of its parent be set to: absolute, fixed, relative or sticky.
 */
export const Ripple = React.memo((props) => {
  const { className, color, focalPoint = { x: 0, y: 0 }, onTransformationEnd } = props;

  return (
    <div className={className}>
      <Transition {...getProps()}>
        <div {...getDotProps()} />
      </Transition>
      <Transition {...getProps(true)}>
        <div {...getDotProps()} />
      </Transition>
    </div>
  );

  function getProps(delayed) {
    const x = getCenter(focalPoint.x);
    const y = getCenter(focalPoint.y);

    return {
      animate: {
        left: [x, x, x],
        opacity: [0.125, 0, 0.125],
        scale: [0, 2, 0],
        top: [y, y, y],
      },
      className: styles.container,
      onTransformationEnd: delayed ? handleTransformationEnd : undefined,
      transition: {
        delay: delayed ? DELAY_FOR_2ND_RIPPLE : undefined,
        duration: STANDARD_DURATION,
        ease: "linear",
        times: [0, 0.99999, 1],
      },
    };
  }

  function getDotProps() {
    return {
      className: styles.dot,
      style: { backgroundColor: getColor() },
    };
  }

  function handleTransformationEnd() {
    onTransformationEnd?.();
  }

  function getCenter(value) {
    return value - DOT_SIZE / 2;
  }

  function getColor() {
    return colors[color] ?? colors[`${color}-500`] ?? getColorVariable(color);
  }
});

Ripple.displayName = "Ripple";

Ripple.propTypes = {
  /**
   * Additional classes for positioning the component. Given classes may only position this component for layout
   * purposes, and cannot change how the component renders in any way.
   */
  className: PropTypes.string,

  /** The ripple dots can use any of the predefined colors in the SWA palette. */
  color: PropTypes.oneOfType([PropTypes.oneOf(Object.keys(colors)), PropTypes.string]),

  /** Position where center of child will render in pixels. */
  focalPoint: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),

  /** Callback that will be called when Ripple animation is complete. No arguments will be returned. */
  onTransformationEnd: PropTypes.func,
};
